import React from "react";
import { Link } from "react-router-dom";

interface NavigationSubItem {
  name: string;
  href?: string;
  current?: boolean;
}

interface NavigationItemProps {
  name: string;
  href?: string;
  current?: boolean;
  children?: NavigationSubItem[];
  isMenuOpen?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onSubItemClick?: () => void;
}

const isExternalHref = (href: string): boolean => /^https?:\/\//.test(href);
const externalAccentClasses = `text-indigo-600 hover:text-indigo-700 dark:text-indigo-300 dark:hover:text-indigo-200 hover:bg-indigo-50 dark:hover:bg-indigo-900/30 inline-flex items-center gap-1`;
const ExternalLinkIcon: React.FC = () => (
  <svg
    className="h-3.5 w-3.5"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M14 5h5v5m0-5L10 14m-4-4v9a1 1 0 001 1h9"
    />
  </svg>
);

const NavigationItem: React.FC<NavigationItemProps> = ({
  name,
  href,
  current,
  children,
  isMenuOpen,
  onMouseEnter,
  onMouseLeave,
  onSubItemClick,
}) => {
  const baseButtonClasses = `px-2 py-2 rounded-md text-sm font-medium transition-colors`;
  const activeClasses = `bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100`;
  const inactiveClasses = `text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white`;
  const buttonClasses = `${baseButtonClasses} ${(current ?? false) ? activeClasses : inactiveClasses}`;

  if (children != null) {
    return (
      <div className="relative" onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}>
        <button
          className={`flex items-center ${buttonClasses}`}
          aria-haspopup="true"
          aria-expanded={isMenuOpen ?? false}
        >
          {name}
          <svg
            className={`ml-1 h-4 w-4 transition-transform ${(isMenuOpen ?? false) ? "rotate-180" : ""}`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {(isMenuOpen ?? false) && (
          <div className="absolute left-0 top-full w-56 pt-1 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
            <div className="py-1">
              {children.map((subItem) =>
                subItem.href != null && isExternalHref(subItem.href) ? (
                  <a
                    key={subItem.name}
                    href={subItem.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`block px-4 py-2 text-sm transition-colors ${externalAccentClasses} ${
                      (subItem.current ?? false) ? "bg-indigo-100 dark:bg-indigo-900/40" : ""
                    }`}
                    onClick={onSubItemClick}
                  >
                    <span>{subItem.name}</span>
                    <ExternalLinkIcon />
                  </a>
                ) : subItem.href != null ? (
                  <Link
                    key={subItem.name}
                    to={subItem.href}
                    className={`block px-4 py-2 text-sm transition-colors ${
                      (subItem.current ?? false)
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                    }`}
                    onClick={onSubItemClick}
                  >
                    {subItem.name}
                  </Link>
                ) : (
                  <span
                    key={subItem.name}
                    className={`block px-4 py-2 text-sm transition-colors ${
                      (subItem.current ?? false)
                        ? "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-100"
                        : "text-gray-700 hover:bg-gray-100 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-700 dark:hover:text-white"
                    }`}
                  >
                    {subItem.name}
                  </span>
                )
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return href != null && isExternalHref(href) ? (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className={`${buttonClasses} ${externalAccentClasses}`}
    >
      <span>{name}</span>
      <ExternalLinkIcon />
    </a>
  ) : href != null ? (
    <Link to={href} className={buttonClasses}>
      {name}
    </Link>
  ) : (
    <span className={buttonClasses}>{name}</span>
  );
};

export default NavigationItem;
